import React, { useEffect, useState } from "react";
import { Plus, Trash2, CalendarHeart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function Timeline() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", event_date: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/timeline");
      setEvents(data);
    } catch (e) { console.error('error in Timeline.jsx', e); }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!form.title || !form.event_date) {
      toast.error("Titre et date requis");
      return;
    }
    setSaving(true);
    try {
      await api.post("/timeline", form);
      toast.success("Moment ajouté à notre histoire ♡");
      setForm({ title: "", description: "", event_date: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Échec de l'ajout");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/timeline/${id}`);
      setEvents((s) => s.filter((x) => x.id !== id));
      toast.success("Moment retiré");
    } catch {
      toast.error("Suppression échouée");
    }
  };

  return (
    <section id="histoire" data-testid="timeline-section" className="px-6 md:px-12 lg:px-24 py-20 lg:py-28 relative">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
          <div>
            <p className="font-accent text-3xl text-rose-300">notre chronologie</p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-rose-50 mt-2">Les moments qui comptent</h2>
            <p className="text-rose-200/70 mt-3 max-w-xl">Chaque date, une étoile dans notre ciel.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="open-timeline-dialog"
                className="bg-rose-600 hover:bg-rose-500 text-white rounded-full px-6 h-12"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouveau moment
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#1A0A0E] border-rose-900/40 text-rose-50">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl">Ajouter un moment</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Titre</Label>
                  <Input
                    data-testid="timeline-title-input"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Notre premier rendez-vous"
                    className="bg-[#0F0407] border-rose-900/40"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    data-testid="timeline-date-input"
                    type="date"
                    value={form.event_date}
                    onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                    className="bg-[#0F0407] border-rose-900/40"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    data-testid="timeline-description-input"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Raconte ce moment..."
                    className="bg-[#0F0407] border-rose-900/40"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  data-testid="timeline-submit-button"
                  onClick={submit}
                  disabled={saving}
                  className="bg-rose-600 hover:bg-rose-500 text-white rounded-full"
                >
                  {saving ? "Sauvegarde..." : "Ajouter"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {events.length === 0 ? (
          <div className="romantic-card rounded-2xl p-12 text-center">
            <CalendarHeart className="w-10 h-10 text-rose-400 mx-auto" />
            <p className="font-accent text-3xl text-rose-300 mt-4">Notre histoire commence ici</p>
            <p className="text-rose-200/60 mt-2">Ajoute le premier moment de notre chronologie.</p>
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-rose-700/60 to-transparent -translate-x-1/2" />
            <div className="space-y-12">
              {events.map((ev, idx) => {
                const left = idx % 2 === 0;
                return (
                  <div key={ev.id} className="relative md:grid md:grid-cols-2 md:gap-12 group">
                    <div className={`absolute left-4 md:left-1/2 top-6 w-3 h-3 rounded-full bg-rose-500 -translate-x-1/2 ring-4 ring-rose-900/40`} />
                    <div className={`pl-12 md:pl-0 ${left ? "md:pr-12 md:text-right" : "md:col-start-2 md:pl-12"}`}>
                      <div className="romantic-card rounded-xl p-6 transition-all hover:-translate-y-1">
                        <div className="flex items-start justify-between gap-3">
                          <div className={left ? "md:ml-auto" : ""}>
                            <p className="font-accent text-2xl text-rose-300">{formatDate(ev.event_date)}</p>
                            <h3 className="font-serif text-2xl text-rose-50 mt-1">{ev.title}</h3>
                          </div>
                          <button
                            data-testid={`timeline-delete-${ev.id}`}
                            onClick={() => remove(ev.id)}
                            className="text-rose-200/40 hover:text-rose-400 transition opacity-0 group-hover:opacity-100"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {ev.description && (
                          <p className="text-rose-100/80 mt-3 leading-relaxed">{ev.description}</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
